import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, User, Lock, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        username: formData.username,
        password: formData.password
      });
      
      // Stocker le token admin
      localStorage.setItem('adminToken', response.data.access_token);
      localStorage.setItem('adminUsername', response.data.username);
      
      toast.success('Connexion admin réussie !');
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Identifiants incorrects';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="p-3 bg-purple-600 rounded-xl">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <div className="text-center">
            <h1 className="font-chivo font-bold text-2xl text-white">
              Administration
            </h1>
            <p className="text-purple-300 text-sm">5minCompta</p>
          </div>
        </div>

        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardHeader className="space-y-1 pb-6 text-center">
            <CardTitle className="font-chivo text-xl">Connexion Admin</CardTitle>
            <CardDescription className="text-slate-500">
              Accès réservé aux administrateurs
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium text-slate-700">
                  Nom d'utilisateur
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="admin"
                    className="pl-10"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    data-testid="admin-username-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Mot de passe
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="admin-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-purple-600 hover:bg-purple-700 h-11 mt-2"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Connexion...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Accéder au dashboard
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>
            </CardContent>
          </form>
        </Card>

        <p className="text-center text-purple-300/60 text-sm mt-6">
          © 2026 5minCompta - Espace Administration
        </p>

        {/* Bouton retour vers login utilisateur */}
        <div className="mt-4 text-center">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-purple-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'espace utilisateur
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
